import React, { useState, useEffect } from "react";
import { Grid, Col, Row, Panel, FormGroup, ControlLabel, FormControl, Form, Button, Alert } from "react-bootstrap";
import Select from "react-select";

import BoomiIcon from "images/boomi.svg";

const BoomiModal = () => {
  const BASE_URL = `https://api.boomi.com/api/rest/v1`;
  const POLL_INTERVAL = 5000;

  const [pollCount, setPollCount] = useState(0);

  const [username, setUsername] = useState("");
  const [debouncedUsername, setDebouncedUsername] = useState("");
  const [password, setPassword] = useState("");
  const [debouncedPassword, setDebouncedPassword] = useState("");
  const [accountId, setAccountId] = useState("");
  const [debouncedAccountId, setDebouncedAccountId] = useState("");

  const [credentialError, setCredentialError] = useState(false);

  const [atomsOptions, setAtomsOptions] = useState([]);
  const [selectedAtom, setSelectedAtom] = useState("");
  const [atomWithEnvList, setAtomWithEnvList] = useState([]);

  const [processOptions, setProcessOptions] = useState([]);
  const [selectedProcess, setSelectedProcess] = useState("");
  const [deployedPackageWithComponentMeta, setDeployedPackageWithComponentMeta] = useState([]);

  const [infoBox, setInfoBox] = useState("");

  const updatePostCredentialInputs = (valid = false) => {
    if (!valid) {
      setAtomsOptions([]);
      setProcessOptions([]);
      setSelectedAtom("");
      setSelectedProcess("");
      setInfoBox("");
    }
  };

  const [currentlyExecutingProcess, setCurrentlyExecutingProcess] = useState(false);

  useEffect(() => {
    const delay = 750;

    if (username !== debouncedUsername) {
      const timerIdUsername = setTimeout(() => {
        setDebouncedUsername(username);
      }, delay);

      return () => {
        clearTimeout(timerIdUsername);
      };
    }
    if (password !== debouncedPassword) {
      const timerIdPassword = setTimeout(() => {
        setDebouncedPassword(password);
      }, delay);

      return () => {
        clearTimeout(timerIdPassword);
      };
    }
    if (accountId !== debouncedAccountId) {
      const timerIdAccountId = setTimeout(() => {
        setDebouncedAccountId(accountId);
      }, delay);

      return () => {
        clearTimeout(timerIdAccountId);
      };
    }
  }, [username, password, accountId]);

  useEffect(() => {
    setCredentialError(false);

    if (debouncedUsername.length && debouncedPassword.length && debouncedAccountId.length) {
      const getAtomList = async () => {
        let environments;

        setAtomsOptions([]);

        try {
          const environmentsResponse = await boomiPost("/environment/query", null);
          environments = JSON.parse(environmentsResponse);
        } catch (e) {
          setCredentialError(true);
          updatePostCredentialInputs();
          return;
        }

        // get environment atom attachments
        const attachmentsResponse = await boomiPost("/EnvironmentAtomAttachment/query", null);
        const attachments = JSON.parse(attachmentsResponse);
        // get atoms
        const atomsResponse = await boomiPost("/Atom/query", null);
        const atoms = JSON.parse(atomsResponse);

        const atomWithEnvList = atoms.result
          .map((atom) => {
            const environmentId = attachments.result.find((entry) => entry.atomId == atom.id).environmentId;
            let environment = null;
            if (environmentId) {
              environment = environments.result.find((entry) => entry.id == environmentId);
            }
            return {
              environment,
              atom
            };
          })
          .filter((entry) => entry.environment);

        setAtomWithEnvList(atomWithEnvList);
        const atomDropdownOptions = atomWithEnvList.map((atomEnv) => {
          return {
            value: atomEnv.atom.id,
            label: `Environment: ${atomEnv.environment.name} (${atomEnv.environment.classification}) - ${atomEnv.atom.name}`
          };
        });
        setAtomsOptions(atomDropdownOptions);
      };

      getAtomList();
    } else {
      updatePostCredentialInputs();
    }
  }, [debouncedUsername, debouncedPassword, debouncedAccountId]);

  useEffect(() => {
    setSelectedProcess("");
    if (selectedAtom && selectedAtom.value) {
      const getDeployedPackages = async () => {
        const atom = atomWithEnvList.find((entry) => entry.atom.id === selectedAtom.value);

        const packagesQuery = {
          QueryFilter: {
            expression: {
              operator: "and",
              nestedExpression: [
                {
                  argument: ["process"],
                  operator: "EQUALS",
                  property: "componentType"
                },
                {
                  argument: [true],
                  operator: "EQUALS",
                  property: "active"
                },
                {
                  argument: [atom.environment.id],
                  operator: "EQUALS",
                  property: "environmentId"
                }
              ]
            }
          }
        };

        const packagesResponse = await boomiPost("/DeployedPackage/query", packagesQuery);

        const packages = JSON.parse(packagesResponse);

        const componentMeta = packages.numberOfResults > 0 ? await getComponentsMetadata(packages) : null;

        const deployedPackageWithComponentMeta = packages.result.map((deployedPackage) => {
          const deployedComponent = componentMeta.result.find(
            (component) =>
              component.componentId == deployedPackage.componentId &&
              component.version == deployedPackage.componentVersion
          );

          return {
            package: deployedPackage,
            component: deployedComponent
          };
        });

        setDeployedPackageWithComponentMeta(deployedPackageWithComponentMeta);

        const processOptions = deployedPackageWithComponentMeta.map((entry) => {
          return {
            value: entry.package.deploymentId,
            label: `${entry.component.name} (Version: ${entry.component.version})`
          };
        });
        setProcessOptions(processOptions);
      };

      setProcessOptions([]);
      getDeployedPackages();
    }
  }, [selectedAtom]);

  useEffect(() => {
    setInfoBox("");
    if (selectedProcess && selectedProcess.value) {
      const checkForLastRunData = async () => {
        const process = deployedPackageWithComponentMeta.find(
          (entry) => entry.package.deploymentId == selectedProcess.value
        ).component;
        const lastExecution = await getLastExecutionRecord(process.componentId);

        if (!lastExecution) {
          setInfoBox("Process has not been run on this atom");
        } else {
          setInfoBox(
            `Last executed ${lastExecution.executionTime} (${
              lastExecution.status
            } in ${lastExecution.executionDuration[1]}ms)`
          );
        }
      };
      checkForLastRunData();
    }
  }, [selectedProcess]);

  const executeOnClick = async (e) => {
    e.preventDefault();

    if (!selectedAtom.value && !selectedProcess.value) {
      return;
    }
    setCurrentlyExecutingProcess(true);
    setInfoBox("Executing process...");
    const process = deployedPackageWithComponentMeta.find(
      (entry) => entry.package.deploymentId == selectedProcess.value
    ).component;

    const atom = atomWithEnvList.find((entry) => entry.atom.id == selectedAtom.value);

    const executePayload = {
      ProcessProperties: {
        "@type": "ProcessProperties",
        ProcessProperty: [
          {
            "@type": "",
            Name: "priority",
            Value: "medium"
          }
        ]
      },
      processId: process.componentId,
      atomId: atom.atom.id
    };

    try {
      await boomiPost("/executeProcess", executePayload);
    } catch (e) {
      executionBox.text(e.message);
      return;
    }

    setPollCount(0);

    setTimeout(() => getPollExecutionStatus(process.componentId), POLL_INTERVAL);
  };

  const getCreds = () => {
    if (!debouncedUsername || !debouncedPassword || !debouncedAccountId) {
      return null;
    }

    return [debouncedUsername, debouncedPassword, debouncedAccountId];
  };

  const getPollExecutionStatus = async (componentId) => {
    const currentPollCount = pollCount + 1;
    setPollCount(currentPollCount);

    const lastExecution = await getLastExecutionRecord(componentId);
    if (lastExecution) {
      setInfoBox(`${lastExecution.executionTime}, ${lastExecution.status}, ${lastExecution.executionDuration[1]}ms`);

      switch (lastExecution.status) {
        case "COMPLETE":
        case "COMPLETE_WARN":
        case "DISCARDED_ERROR":
        case "ERROR":
        case "ABORTED":
          // These are all terminal states - stop polling
          setCurrentlyExecutingProcess(false);
          return;
      }
    }

    if (currentPollCount > 50) {
      // Give up
      setInfoBox(`Not completed. Please check Boomi for status.`);
      return;
    }
    setTimeout(() => getPollExecutionStatus(componentId), POLL_INTERVAL);
  };

  const boomiPost = async (target, payloadObj) => {
    const [username, password, accountId] = getCreds();

    const basicAuth = Buffer.from(`${username}:${password}`).toString("base64");

    const additionalHeaders = {
      authorization: `Basic ${basicAuth}`,
      accept: "application/json", // required, otherwise Boomi returns XML
      "content-type": "application/json"
    };

    const payload = payloadObj ? JSON.stringify(payloadObj) : null;

    const url = `${BASE_URL}/${accountId}${target}`;

    let resp = await fetch(url, {
      method: "POST",
      headers: additionalHeaders,
      body: payload
    });

    if (resp.status == 403) {
      throw new Error("Failed to connect to Boomi with given credentials");
    } else if (resp.status > 299) {
      throw new Error(`${resp.status} - ${resp.statusMessage}`);
    }

    //carryover comment from prototype, not sure if actually necessary
    // Hacky workaround
    // The Http Client (and Rest one for that matter) overrides the accept-encoding headers to gzip, deflate, br
    // The result comes back with content-encoding header set to gzip but the content is plain. No idea what is going on as same
    // request with curl comes back gzipped with gzip header. weird.
    // Sadly because of this I have to use the HttpClient instead of the RestClient and have to manually convert the JSON
    resp.headers["content-encoding"] = "";

    const body = await resp.text();

    return body;
  };

  const getComponentsMetadata = async (packages) => {
    const nestedExpression = packages.result.reduce((agg, deployedPackage) => {
      agg.push({
        operator: "and",
        nestedExpression: [
          {
            argument: [deployedPackage.componentId],
            operator: "EQUALS",
            property: "componentId"
          },
          {
            argument: [deployedPackage.componentVersion],
            operator: "EQUALS",
            property: "version"
          }
        ]
      });
      return agg;
    }, []);
    const componentsQuery = {
      QueryFilter: {
        expression: {
          operator: "or",
          nestedExpression
        }
      }
    };

    const componentsResponse = await boomiPost("/ComponentMetadata/query", componentsQuery);

    return JSON.parse(componentsResponse);
  };

  const getLastExecutionRecord = async (componentId) => {
    const executionsQuery = {
      QueryFilter: {
        expression: {
          argument: [componentId],
          operator: "EQUALS",
          property: "processId"
        }
      }
    };

    const executionRecordResponse = await boomiPost("/ExecutionRecord/query", executionsQuery);

    const executions = JSON.parse(executionRecordResponse);

    if (!executions.result.length) {
      return null;
    } else {
      const lastExecution = executions.result.reduce((last, curr) => {
        return curr.executionTime.localeCompare(last.executionTime) ? curr : last;
      }, executions.result[0]);
      return lastExecution;
    }
  };

  const onAtomDropdownChange = (e) => {
    setSelectedAtom(e);
  };

  const onProcessDropdownChange = (e) => {
    setSelectedProcess(e);
  };

  return (
    <React.Fragment>
      <Grid fluid>
        <Row>
          <Col>
            <div style={{ float: "right" }}>
              <img
                src={BoomiIcon}
                className="img"
                style={{ height: "50px", marginBottom: "10px" }}
                alt="boomi logo"
              ></img>
            </div>
          </Col>
        </Row>
        <Row>
          <Col>
            <Panel>
              <Panel.Body>
                <Form horizontal>
                  <FormGroup>
                    <Col componentClass={ControlLabel} sm={3}>
                      <div style={{ textAlign: "center" }}>Username</div>
                    </Col>
                    <Col sm={9}>
                      <FormControl
                        type="text"
                        placeholder="Boomi platform username"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                      />
                    </Col>
                  </FormGroup>
                  <FormGroup>
                    <Col componentClass={ControlLabel} sm={3}>
                      <div style={{ textAlign: "center" }}>Password</div>
                    </Col>
                    <Col sm={9}>
                      <FormControl
                        type="password"
                        placeholder="Boomi platform password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                      />
                    </Col>
                  </FormGroup>
                  <FormGroup controlId="accountIdText">
                    <Col componentClass={ControlLabel} sm={3}>
                      <div style={{ textAlign: "center" }}>Account ID</div>
                    </Col>
                    <Col sm={9}>
                      <FormControl
                        type="text"
                        placeholder="Boomi platform accountId"
                        value={accountId}
                        onChange={(e) => setAccountId(e.target.value)}
                      />
                    </Col>
                  </FormGroup>
                  <FormGroup>
                    <Col sm={3}></Col>
                    <Col sm={9}>
                      {credentialError ? <Alert bsStyle="danger">Failed to validate credentials</Alert> : null}
                    </Col>
                  </FormGroup>
                  <div style={{ marginTop: "36px" }}>
                    <FormGroup>
                      <Col componentClass={ControlLabel} sm={3}>
                        <div style={{ textAlign: "center" }}>Atom</div>
                      </Col>
                      <Col sm={9}>
                        <Select
                          isDisabled={atomsOptions.length ? false : true}
                          options={atomsOptions}
                          placeholder="Select atom..."
                          value={selectedAtom}
                          onChange={onAtomDropdownChange}
                        />
                      </Col>
                    </FormGroup>
                    <FormGroup>
                      <Col componentClass={ControlLabel} sm={3}>
                        <div style={{ textAlign: "center" }}>Process</div>
                      </Col>
                      <Col sm={9}>
                        <Select
                          isDisabled={processOptions.length || (selectedAtom && selectedAtom.value) ? false : true}
                          options={processOptions}
                          placeholder="Select process..."
                          value={selectedProcess}
                          onChange={onProcessDropdownChange}
                        />
                      </Col>
                    </FormGroup>
                  </div>
                  <FormGroup>
                    <Col sm={3}></Col>
                    <Col sm={7}>
                      {infoBox.length ? <p style={{ fontStyle: "italic", marginTop: "6px" }}>{infoBox}</p> : null}
                    </Col>
                    <Col sm={2}>
                      <div style={{ float: "right" }}>
                        <Button
                          bsStyle="primary"
                          disabled={
                            selectedProcess && selectedProcess.value && !currentlyExecutingProcess ? false : true
                          }
                          onClick={executeOnClick}
                        >
                          {currentlyExecutingProcess ? "Executing process..." : "Execute"}
                        </Button>
                      </div>
                    </Col>
                  </FormGroup>
                </Form>
              </Panel.Body>
            </Panel>
          </Col>
        </Row>
      </Grid>
    </React.Fragment>
  );
};

export default BoomiModal;
